import React from "react"
import { useTranslation } from "react-i18next"

import { HStack } from "@/nzds/layout"
import { singleLineEllipsisStyles } from "@/styles/utils"
import type { UploadFileType } from "@/utils/upload-file-type"
import { isAcceptedUploadFile } from "@/utils/upload-file-type"
import { filesize } from "filesize"

import CloseIconRounded from "@mui/icons-material/CloseRounded"
import UploadIcon from "@mui/icons-material/Upload"
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
  styled,
} from "@mui/material"

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
})

interface UploadFileProps {
  acceptedFileTypes: UploadFileType[]
  isDisabled?: boolean
  multiple?: boolean
  onChange: (files: File[]) => void
}

export const UploadFile: React.FC<UploadFileProps> = ({
  acceptedFileTypes,
  isDisabled,
  multiple = false,
  onChange,
}) => {
  const { t } = useTranslation("common")
  const [files, setFiles] = React.useState<File[]>([])
  const [isDragOver, setIsDragOver] = React.useState(false)

  const isValidUploadFile = (inputFile: File): boolean =>
    isAcceptedUploadFile(inputFile, acceptedFileTypes)

  const setFilesAndNotify = (nextFiles: File[]): void => {
    setFiles(nextFiles)
    onChange(nextFiles)
  }

  const selectedFile = files[0]
  const fileSize: string = selectedFile
    ? filesize(selectedFile.size, { standard: "jedec" })
    : ""

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const selectedFiles = Array.from(event.target.files ?? []).filter(
      (inputFile) => isValidUploadFile(inputFile)
    )

    if (selectedFiles.length > 0) {
      setFilesAndNotify(multiple ? selectedFiles : [selectedFiles[0]])
    }

    event.target.value = ""
  }

  const handleRemoveFileClick = (): void => {
    setFilesAndNotify([])
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    if (isDisabled) {
      return
    }

    setIsDragOver(true)
    event.dataTransfer.dropEffect = "copy"
  }

  const handleDragLeave = (): void => {
    setIsDragOver(false)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    setIsDragOver(false)

    if (isDisabled) {
      return
    }

    const droppedFiles = Array.from(event.dataTransfer.files).filter(
      (droppedFile) => isValidUploadFile(droppedFile)
    )
    if (droppedFiles.length > 0) {
      setFilesAndNotify(multiple ? droppedFiles : [droppedFiles[0]])
    }
  }

  return (
    // overflow ensures the drag and drop outline is fully visible
    <Card variant="contained" sx={{ overflow: "visible" }}>
      <CardContent
        data-testid="upload-file-dropzone"
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          borderColor: isDragOver ? "primary.main" : "transparent",
          borderRadius: 1,
          borderStyle: "dashed",
          borderWidth: 1,
          transition: "border-color 150ms ease",
        }}
      >
        {selectedFile ? (
          <HStack
            sx={{
              alignItems: "center",
              gap: 2,
              height: "40px",
              justifyContent: "space-between",
            }}
          >
            <HStack sx={{ alignItems: "baseline", gap: 2, minWidth: 0 }}>
              <Typography
                sx={{
                  ...singleLineEllipsisStyles,
                }}
              >
                {files.length > 1
                  ? files.map((file) => file.name).join(", ")
                  : selectedFile.name}
              </Typography>
              {files.length === 1 && (
                <Typography sx={{ whiteSpace: "nowrap" }} variant="body2">
                  {fileSize}
                </Typography>
              )}
            </HStack>
            <IconButton
              aria-label={t("button.removeFile", {
                fileName: selectedFile.name,
              })}
              disabled={isDisabled}
              onClick={handleRemoveFileClick}
              size="small"
            >
              <CloseIconRounded />
            </IconButton>
          </HStack>
        ) : (
          <Stack
            sx={{
              alignItems: "center",
              gap: 1,
              p: 1,
            }}
          >
            <Stack
              sx={{
                alignItems: "center",
              }}
            >
              <Typography variant="body1">
                {t("upload.dragAndDropFileLabel")}
              </Typography>
              <Typography variant="caption">
                {t("upload.dragAndDropFileLabelCaption")}
              </Typography>
            </Stack>
            <Box>
              <Button
                component="label"
                disabled={isDisabled}
                role={undefined}
                startIcon={<UploadIcon />}
                tabIndex={-1}
                variant="outlined"
              >
                {t("upload.chooseFileButton")}
                <VisuallyHiddenInput
                  accept={acceptedFileTypes.map((type) => `.${type}`).join(",")}
                  aria-label={t("upload.chooseFileAriaLabel")}
                  multiple={multiple}
                  type="file"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}
